import { internalFetch as fetch } from './internalFetch';
import { stripLoginId } from './utils';

/** Base URL for Options REST; non-JSON bodies throw `DerivHttpError` from `internalFetch`. */
const BASE_URL = 'https://api.derivws.com';

/**
 * Build standard headers for Deriv REST API calls.
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv application ID
 * @param {boolean} [json=false] - Whether to include Content-Type: application/json
 * @returns {Object} Headers object
 */
const buildHeaders = (bearer, appId, json = false) => {
    const headers = {
        'Authorization': `Bearer ${bearer}`,
        'Deriv-App-ID': appId,
    };
    if (json) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

/**
 * Build headers for REST endpoints that authenticate via Deriv-App-ID only (no OAuth bearer).
 * @param {string|number} appId - Deriv application ID
 * @param {boolean} [json=false] - Whether to include Content-Type: application/json
 * @returns {Object} Headers object
 */
const buildAppIdHeaders = (appId, json = false) => {
    const headers = {
        'Deriv-App-ID': String(appId),
    };
    if (json) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

/**
 * Get all accounts for the authenticated user.
 *
 * @param {string} bearer - OAuth access token (ory_at_...)
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ data: Array<{ account_id: string, balance: string, currency: string, group: string, status: string, account_type: string }>, meta: Object }>}
 */
export const getAccounts = async (bearer, appId) => {
    const response = await fetch(`${BASE_URL}/trading/v1/options/accounts`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

/**
 * Create a new trading account.
 *
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @param {string} accountType - Account type ('real' or 'demo')
 * @param {string} currency - Account currency ('USD', etc.)
 * @param {string} group - Account group ('row', etc.)
 * @returns {Promise<{ data: { account_id: string, balance: number, currency: string, group: string, status: string, account_type: string }, meta: Object }>}
 */
export const createAccount = async (bearer, appId, accountType, currency, group) => {
    const response = await fetch(`${BASE_URL}/trading/v1/options/accounts`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify({
            account_type: accountType,
            currency: currency,
            group: group,
        }),
    });
    return response.json();
};

/**
 * Get a one-time-password WebSocket URL for an account.
 * The returned URL is dynamic and single-use — use it immediately to connect.
 *
 * @param {string} accountId - The account ID (e.g. 'ROT90051085')
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<string>} The full WebSocket URL (wss://api.derivws.com/trading/v1/options/ws/real?otp=XXX)
 */
export const getOTPWebSocketUrl = async (accountId, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/trading/v1/options/accounts/${accountId}/otp`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId),
    });
    const data = await response.json();

    if (!data.data || !data.data.url) {
        throw new Error(`Failed to get OTP WebSocket URL: ${JSON.stringify(data)}`);
    }

    return data.data.url;
};

/**
 * Reset a demo account balance to $10,000 USD (v4 Options API only).
 *
 * @param {string} accountId - The demo account ID (e.g. 'ROT90051085')
 * @param {string} bearer - OAuth access token (ory_at_...)
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<any>}
 */
export const resetDemoBalance = async (accountId, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/trading/v1/options/accounts/${accountId}/reset-demo-balance`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

/**
 * Get aggregated markup and trading statistics for registered applications (v4 REST).
 * Requires OAuth2 scope: application_read.
 *
 * @param {string} dateFrom - Interval start date (YYYY-MM-DD, inclusive, UTC)
 * @param {string} dateTo - Interval end date (YYYY-MM-DD, inclusive, UTC)
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<any>}
 */
export const getMarkupStatistics = async (dateFrom, dateTo, bearer, appId) => {
    const url = new URL(`${BASE_URL}/applications/v1/markup-statistics`);
    url.searchParams.set('date_from', dateFrom);
    url.searchParams.set('date_to', dateTo);
    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

/**
 * Buy the same options contract for multiple end-user accounts in a single call (v4 REST).
 * V2 successor to legacy WebSocket `buy_contract_for_multiple_accounts`.
 * No OAuth access token is required — each account is authorized by its own PAT in the request body.
 *
 * @param {string|number} appId - Deriv application ID (`Deriv-App-ID` header)
 * @param {'real'|'demo'} accountType - Target account type (`real` or `demo`)
 * @param {Object} contractParameters - Contract parameters applied to every account (e.g. contract_type, symbol, amount, duration)
 * @param {Array<{ token: string, account_id: string }>} accounts - Up to 100 token/account pairs
 * @returns {Promise<{ data: { transactions: Array }, meta: Object }>}
 */
export const bulkPurchase = async (appId, accountType, contractParameters, accounts) => {
    const response = await fetch(`${BASE_URL}/trading/v1/options/contracts/bulk-purchase/${accountType}`, {
        method: 'POST',
        headers: buildAppIdHeaders(appId, true),
        body: JSON.stringify({
            contract_parameters: stripLoginId(contractParameters),
            accounts,
        }),
    });
    return response.json();
};

/**
 * Liveness probe for the Options API.
 *
 * `/v1/health` answers `text/plain` with the bare string `OK` — it is the one endpoint here
 * that is not JSON. Reading it with `.json()` therefore always threw `DerivHttpError` via
 * `throwDerivHttpBadJson`, so this call could never succeed; read the body as text instead.
 *
 * @returns {Promise<string>} Raw body, `'OK'` when healthy.
 */
export const getSystemHealth = async () => {
    const response = await fetch(`${BASE_URL}/v1/health`, { method: 'GET' });
    return response.text();
};

export const getAccountNickname = async (bearer, appId) => {
    const response = await fetch(`${BASE_URL}/account/v1/nickname`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const getWallets = async (bearer, appId) => {
    const response = await fetch(`${BASE_URL}/wallet/v1/wallets`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const getWalletTransactions = async (walletType, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/wallet/v1/transactions/${walletType}`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const getPaymentAgentClientSettings = async (bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/clients/me`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const updatePaymentAgentClientSettings = async (settings, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/clients/me`, {
        method: 'PATCH',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(settings),
    });
    return response.json();
};

export const getPaymentAgent = async (agentId, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/agents/${agentId}`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const getPaymentAgents = async (bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/agents`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const getPaymentAgentStatistics = async (bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/agent-statistics`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const paymentAgentTransferRest = async (transferData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/transfer`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(transferData),
    });
    return response.json();
};

export const getPaymentAgentTransferStatus = async (requestId, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/transfer/${requestId}`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const paymentAgentWithdrawRest = async (withdrawData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/withdraw`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(withdrawData),
    });
    return response.json();
};

export const getPaymentAgentWithdrawStatus = async (requestId, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/withdraw/${requestId}`, {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

export const verifyPaymentAgentWithdrawCode = async (verificationData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/payment-agents/v1/withdraw/verification_code`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(verificationData),
    });
    return response.json();
};

/**
 * Get the current exchange rate between two currencies (v4 Wallet REST).
 *
 * @param {string} sourceCurrency - Currency to convert from (e.g. 'USD')
 * @param {string} destinationCurrency - Currency to convert to (e.g. 'BTC')
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ data: { exchange_rate: string, rate_token: string } }>}
 */
export const getExchangeRate = async (sourceCurrency, destinationCurrency, bearer, appId) => {
    const url = new URL(`${BASE_URL}/wallet/v1/exchange-rate`);
    url.searchParams.set('source_currency', sourceCurrency);
    url.searchParams.set('destination_currency', destinationCurrency);
    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

/**
 * Transfer funds between two wallets (v4 Wallet REST).
 *
 * @param {{ currency: string, amount: string, source_wallet_id: string, destination_wallet_id: string, request_id: string, description?: string }} transferData
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ data: { request_id: string } }>}
 */
export const transferBetweenWallets = async (transferData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/wallet/v1/transfers`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(transferData),
    });
    return response.json();
};

/**
 * Transfer funds between two wallets with a currency exchange (v4 Wallet REST).
 * Requires a prior `getExchangeRate()` call for `exchange_rate` and `rate_token`.
 *
 * @param {{ source_wallet_id: string, source_currency: string, amount: string, destination_wallet_id: string, destination_currency: string, exchange_rate: string, rate_token: string, request_id: string, description?: string }} transferData
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ data: { request_id: string } }>}
 */
export const exchangeTransfer = async (transferData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/wallet/v1/transfers/exchange`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(transferData),
    });
    return response.json();
};

/**
 * Transfer funds between a wallet and a trading platform account (v4 Wallet REST).
 *
 * @param {{ wallet_id: string, amount: string, currency: string, direction: 'to_wallet'|'from_wallet', platform_name: 'mt5'|'ctrader'|'options'|'crypto-exchange'|'tradingview', platform_account_id: string, request_id: string, description?: string, wallet_currency?: string, exchange_rate?: string, rate_token?: string }} transferData
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ data: { status: Object, request_id: string } }>}
 */
export const transferToPlatform = async (transferData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/wallet/v1/transfers/platforms`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(transferData),
    });
    return response.json();
};

/**
 * Validate a transfer before executing it (v4 Wallet REST). No funds move.
 *
 * @param {{ source_type: 'main'|'p2p'|'platform'|'payment_agent', destination_type: 'main'|'p2p'|'platform'|'payment_agent', source_id: string, destination_id: string, amount: string, balance: string, source_currency: string, destination_currency: string, rate?: string, source_platform_name?: string, destination_platform_name?: string }} transferData
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ data: { is_valid: boolean, details: Object } }>}
 */
export const validateTransfer = async (transferData, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/wallet/v1/transfers/validate`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify(transferData),
    });
    return response.json();
};

/**
 * Get partner analytics overview for a date range (v4 Partners REST).
 *
 * @param {string} startDate - Interval start date (YYYY-MM-DD)
 * @param {string} endDate - Interval end date (YYYY-MM-DD)
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<any>}
 */
export const getPartnerAnalyticsOverview = async (startDate, endDate, bearer, appId) => {
    const url = new URL(`${BASE_URL}/partners/analytics/v1/overview`);
    url.searchParams.set('start_date', startDate);
    url.searchParams.set('end_date', endDate);
    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: buildHeaders(bearer, appId),
    });
    return response.json();
};

/**
 * Check which client IDs are tagged to this partner (v4 Partners REST).
 *
 * @param {string[]} clientIds - 1-100 client IDs (numeric, UUID, or alphanumeric)
 * @param {string} bearer - OAuth access token
 * @param {string} appId - Deriv client/app ID
 * @returns {Promise<{ partner_id: number, total_checked: number, tagged_count: number, clients: Array<{ client_id: string, is_tagged: boolean }> }>}
 */
export const checkClientTags = async (clientIds, bearer, appId) => {
    const response = await fetch(`${BASE_URL}/partners/client-tags/check`, {
        method: 'POST',
        headers: buildHeaders(bearer, appId, true),
        body: JSON.stringify({ client_ids: clientIds }),
    });
    return response.json();
};

export const _test_buildAppIdHeaders = buildAppIdHeaders;


